import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { WorkerAttr } from '@/types/worker-attr';

/**
 * Hook that provides base worker attributes (hardcoded defaults).
 * These are always available regardless of API response.
 *
 * TODO: Add API integration later to fetch custom attributes
 */
export function useBaseWorkerAttributes(): WorkerAttr[] {
  const intl = useIntl();

  return useMemo(
    () => [
      // Worker attributes
      {
        id: 'lastname',
        label: intl.formatMessage({
          defaultMessage: 'talent last name',
          description:
            'Variable label in message editor: applicant family name merge field',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'firstname',
        label: intl.formatMessage({
          defaultMessage: 'talent first name',
          description:
            'Variable label in message editor: applicant given name merge field',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'displayFullName',
        label: intl.formatMessage({
          defaultMessage: 'talent name',
          description:
            'Variable label in message editor: applicant full display name',
        }),
        dataType: 'string',
        fallback: '',
        tag: 'worker',
      },
      {
        id: 'startDate',
        label: intl.formatMessage({
          defaultMessage: 'talent start date',
          description:
            'Variable label in message editor: applicant employment start date',
        }),
        fallback: '',
        dataType: 'date',
        tag: 'worker',
      },
      {
        id: 'personalEmail.email',
        label: intl.formatMessage({
          defaultMessage: 'talent personal email',
          description:
            'Variable label in message editor: applicant personal email address',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'workEmail.email',
        label: intl.formatMessage({
          defaultMessage: 'talent work email',
          description:
            'Variable label in message editor: applicant work email address',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'mobilePhone.number',
        label: intl.formatMessage({
          defaultMessage: 'talent phone',
          description:
            'Variable label in message editor: applicant phone number',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'job.name',
        label: intl.formatMessage({
          defaultMessage: 'talent job name',
          description:
            'Variable label in message editor: job title associated with applicant',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'locations[0].name',
        label: intl.formatMessage({
          defaultMessage: 'talent location name',
          description:
            'Variable label in message editor: primary work location name',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'locations[0].street_address',
        label: intl.formatMessage({
          defaultMessage: 'talent location address',
          description:
            'Variable label in message editor: street address of work location',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'locations[0].city',
        label: intl.formatMessage({
          defaultMessage: 'talent location city',
          description:
            'Variable label in message editor: city of work location',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'locations[0].state_code',
        label: intl.formatMessage({
          defaultMessage: 'talent location state',
          description:
            'Variable label in message editor: state/region code for work location',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'homeAddress.address1',
        label: intl.formatMessage({
          defaultMessage: 'talent home address1',
          description:
            'Variable label in message editor: first line of applicant home street address',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'homeAddress.address2',
        label: intl.formatMessage({
          defaultMessage: 'talent home address2',
          description:
            'Variable label in message editor: second line of applicant home street address',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'homeAddress.state',
        label: intl.formatMessage({
          defaultMessage: 'talent home state',
          description:
            'Variable label in message editor: state/region on applicant home address',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'homeAddress.city',
        label: intl.formatMessage({
          defaultMessage: 'talent home city',
          description:
            'Variable label in message editor: city on applicant home address',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'uuid',
        label: intl.formatMessage({
          defaultMessage: 'talent id',
          description:
            'Variable label in message editor: internal applicant record ID',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'applicantId',
        label: intl.formatMessage({
          defaultMessage: 'talent applicant id',
          description:
            'Variable label in message editor: external or display applicant identifier',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'dashboardUrl.url',
        label: intl.formatMessage({
          defaultMessage: 'talent portal link',
          description:
            'Variable label in message editor: URL to applicant-facing portal',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        // this is a constructed value that should only be used in the context
        // of sending messages to an employer/worker-adjacent user
        id: 'EMPLOYER_ONLY_workerProfileUrl',
        label: intl.formatMessage({
          defaultMessage: 'talent profile link',
          description:
            'Variable label in message editor: URL to applicant profile (employer-only token)',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      // Company attributes
      {
        id: 'address.address1',
        label: intl.formatMessage({
          defaultMessage: 'company address1',
          description:
            'Variable label in message editor: first line of employer company street address',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'company',
      },
      {
        id: 'address.address2',
        label: intl.formatMessage({
          defaultMessage: 'company address2',
          description:
            'Variable label in message editor: second line of employer company street address',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'company',
      },
      {
        id: 'address.city',
        label: intl.formatMessage({
          defaultMessage: 'company city',
          description:
            'Variable label in message editor: employer company city',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'company',
      },
      {
        id: 'address.country',
        label: intl.formatMessage({
          defaultMessage: 'company country',
          description:
            'Variable label in message editor: employer company country',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'company',
      },
      {
        id: 'address.state',
        label: intl.formatMessage({
          defaultMessage: 'company state',
          description:
            'Variable label in message editor: employer company state or region',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'company',
      },
      {
        id: 'address.zip',
        label: intl.formatMessage({
          defaultMessage: 'company zip',
          description:
            'Variable label in message editor: employer company postal code',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'company',
      },
      {
        id: 'name',
        label: intl.formatMessage({
          defaultMessage: 'company name',
          description:
            'Variable label in message editor: employer organization display name',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'company',
      },
      {
        id: 'legalName',
        label: intl.formatMessage({
          defaultMessage: 'company legal name',
          description:
            'Variable label in message editor: employer registered legal name',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'company',
      },
      // Brand attributes
      {
        id: 'name',
        label: intl.formatMessage({
          defaultMessage: 'brand name',
          description:
            'Variable label in message editor: sub-brand or career-site brand name',
        }),
        fallback: '',
        dataType: 'string',
        tag: 'brand',
      },
    ],
    [intl],
  );
}
